"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getTopTags } from "@/lib/blog/static";
import type { BlogPostTagWithPostsCount } from "@/lib/supabase/types";
import Link from "next/link";
import { useEffect, useState } from "react";
import { EmojiFromCode } from "../ui/Emojis";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

interface TopTrendingTagsProps {
	className?: string;
	variant?: string;
}

export default function TopTrendingTags({
	className,
	variant,
}: TopTrendingTagsProps) {
	const [tags, setTags] = useState<BlogPostTagWithPostsCount[] | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchTags = async () => {
			try {
				const { tags: topTags, error } = await getTopTags(10);
				if (error) {
					console.error("Error fetching top tags:", error);
					return;
				}
				if (topTags) {
					setTags(topTags);
				}
			} catch (error) {
				console.error("Error fetching top tags:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchTags();
	}, []);

	const formatCount = (count: number) => {
		if (count >= 1000000) {
			return `${(count / 1000000).toFixed(1)}M`;
		}
		if (count >= 1000) {
			return `${(count / 1000).toFixed(1)}K`;
		}
		return count.toString();
	};

	const renderTags = () => {
		if (loading) {
			return [...Array(5)].map((_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
				<div key={i} className="flex items-center justify-between">
					<div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
					<div className="h-3 bg-gray-200 rounded w-12 animate-pulse" />
				</div>
			));
		}
		if (tags && tags.length > 0) {
			return tags.map((tag) => (
				<Button
					key={tag.id}
					variant="ghost"
					className="relative inline-flex rounded-full items-center justify-between px-2 py-0"
				>
					<Link href={`/blog?tags=${tag.name}`}>
						<span className="font-semibold text-xs sm:text-sm text-primary">
							#{tag.name}
						</span>

						<Badge
							variant="default"
							className="absolute -top-1 -right-1 text-[9px] px-1 py-0 rounded-full bg-primary/40 text-white text-xs"
						>
							{formatCount(tag.posts_count)}
						</Badge>
					</Link>
				</Button>
			));
		}
		return (
			<p className="text-muted-foreground text-sm">No trending tags found</p>
		);
	};

	if (variant === "slim") {
		return (
			<div className="w-full flex flex-row gap-2 items-center justify-between md:justify-end p-1">
				{renderTags()}
			</div>
		);
	}

	return (
		<Card className={className}>
			<CardHeader className="py-3 items-center justify-center text-center space-y-4">
				<CardTitle className="flex gap-2 text-nowrap text-2xl font-extrabold tracking-tighter">
					Trending
					<EmojiFromCode symbol="0x1F446" className="inline" label="Trending" />
				</CardTitle>
			</CardHeader>
			<CardContent className="px-2">
				<div className="h-full items-center justify-center">{renderTags()}</div>
			</CardContent>
		</Card>
	);
}
